import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageCTA from "@/components/PageCTA";
import { formatApiError } from "@/context/AuthContext";
import { Instagram, Youtube, Mail, ArrowRight } from "lucide-react";
import { useContent } from "@/lib/useContent";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [busy, setBusy] = useState(false);
    const { data: copy } = useContent("contact_copy", {
        overline: "Contact",
        title: "Say hello.",
        lede: "",
        email: "hello@dtscotslad.com",
        instagram_handle: "@dtscotslad",
        youtube_handle: "DTScotslad Films",
        office: "Stirling, Scotland",
    });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/contact", form);
            toast.success("Message away. I'll get back to you within a few days.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="contact-page">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                <div className="lg:col-span-5">
                    <Reveal>
                        <div className="overline mb-5"><span className="editorial-rule" />{copy.overline}</div>
                    </Reveal>
                    <h1
                        className="font-serif font-light tracking-tighter leading-[0.9] mb-10"
                        style={{ fontSize: "clamp(3rem, 8vw, 7.5rem)" }}
                        data-testid="contact-title"
                    >
                        <div>
                            <MaskedLine delay={0.1}>
                                <span className="italic text-[var(--sb-brand)]">{copy.title}</span>
                            </MaskedLine>
                        </div>
                    </h1>
                    <Reveal delay={0.35}>
                        <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed mb-10">
                            {copy.lede}
                        </p>
                    </Reveal>
                    <Reveal delay={0.5}>
                        <div className="space-y-4 text-[var(--sb-text-dim)]">
                            <a href={`mailto:${copy.email}`} className="flex items-center gap-3 hover:text-[var(--sb-text)] transition-colors group" data-testid="contact-email">
                                <span className="w-6 h-px bg-[var(--sb-brand)] group-hover:w-10 transition-all duration-500" />
                                <Mail size={18} className="text-[var(--sb-brand)]" /> {copy.email}
                            </a>
                            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-[var(--sb-text)] transition-colors group" data-testid="contact-instagram">
                                <span className="w-6 h-px bg-[var(--sb-brand)] group-hover:w-10 transition-all duration-500" />
                                <Instagram size={18} className="text-[var(--sb-brand)]" /> {copy.instagram_handle}
                            </a>
                            <a href="https://youtube.com" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-[var(--sb-text)] transition-colors group" data-testid="contact-youtube">
                                <span className="w-6 h-px bg-[var(--sb-brand)] group-hover:w-10 transition-all duration-500" />
                                <Youtube size={18} className="text-[var(--sb-brand)]" /> {copy.youtube_handle}
                            </a>
                        </div>
                        <div className="divider my-10" />
                        <p className="text-xs uppercase tracking-[0.28em] text-[var(--sb-text-muted)] mb-2">Office</p>
                        <p className="text-[var(--sb-text-dim)]">{copy.office}</p>
                    </Reveal>
                </div>

                <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7">
                    <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                        <div>
                            <label className="overline block mb-2">Your name</label>
                            <input className="input-line" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
                        </div>
                        <div>
                            <label className="overline block mb-2">Email</label>
                            <input type="email" className="input-line" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
                        </div>
                        <div>
                            <label className="overline block mb-2">Your message</label>
                            <textarea required minLength={5} className="input-line" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
                        </div>
                        <button type="submit" disabled={busy} className="btn-primary" data-testid="contact-submit">
                            {busy ? "Sending…" : "Send message"} <ArrowRight size={18} />
                        </button>
                    </form>
                </Reveal>
            </div>
            <PageCTA variant="flight" className="!px-0" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}
